// One conversation open: the messages, internal notes, the teams on it, reply, close and reopen. Shared by Messages
// and a development's Property management → Messages.
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowLeft, Check, CheckCheck, Loader2, Lock, Send } from 'lucide-react';
import { Segmented, Badge, Button, Modal, Field, Input, Textarea, EmptyState } from '../ui';
import { FormError } from '../forms/common';
import { TeamsStrip } from './TeamsStrip';
import { messagesApi, TEAM_COLOUR, whenLong, type ConversationDetail } from '../../lib/comms';

export function Thread({ id, onBack, onChanged }: { id: string; onBack: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const [internal, setInternal] = useState(false);
  const [text, setText] = useState('');
  const [closing, setClosing] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: ['/messages', id], queryFn: () => messagesApi.get(id), refetchInterval: 20_000 });
  const c = q.data;

  // Opening a thread reads it.
  const unread = c?.unread ?? 0;
  useEffect(() => {
    if (!c || unread === 0) return;
    void messagesApi.read(c.id).then(() => { onChanged(); void qc.invalidateQueries({ queryKey: ['/messages/unread'] }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.id, unread]);
  useEffect(() => { logRef.current?.scrollTo({ top: logRef.current.scrollHeight }); }, [c?.messages.length]);
  useEffect(() => { setInternal(false); setText(''); }, [id]);

  const set = (d: ConversationDetail) => { qc.setQueryData(['/messages', id], d); onChanged(); };
  const send = useMutation({
    mutationFn: () => messagesApi.reply(id, text.trim(), internal),
    onSuccess: (d) => { setText(''); set(d); },
  });
  const reopen = useMutation({ mutationFn: () => messagesApi.reopen(id), onSuccess: set });

  if (q.isLoading) return <div className="surface h-[420px] animate-pulse" />;
  if (q.error || !c) return <EmptyState icon="TriangleAlert" title="Could not open this conversation" body={(q.error as Error | null)?.message} action={<Button variant="secondary" onClick={onBack}>Back</Button>} />;
  const staff = c.viewer.side === 'STAFF';

  return (
    <div className="surface flex min-h-[420px] flex-col overflow-hidden lg:h-[calc(100vh-17.5rem)]">
      <div className="flex items-start gap-3 border-b border-ink-100 px-4 py-3">
        <button onClick={onBack} className="-ml-1 grid h-8 w-8 place-items-center rounded-lg text-ink-500 hover:bg-ink-100 lg:hidden" aria-label="Back"><ArrowLeft className="h-4 w-4" /></button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold leading-tight text-ink-900">{c.subject}</div>
          <div className="mt-0.5 text-[12px] text-ink-500">
            {staff ? <>{c.residentNames ?? 'Resident'} · </> : null}
            {[c.unitReference, c.propertyName].filter(Boolean).join(', ')} · to <span style={{ color: TEAM_COLOUR[c.team] }}>{c.teamLabel}</span>
            {c.tenancyReference && staff ? <> · {c.tenancyReference}</> : null}
          </div>
        </div>
        <Badge value={c.status} tone={c.status === 'OPEN' ? 'good' : 'neutral'} />
      </div>
      {staff && <TeamsStrip c={c} colour={TEAM_COLOUR} onChanged={set} />}

      <div ref={logRef} className="flex-1 space-y-3 overflow-y-auto bg-ink-50/40 px-3 py-4 sm:px-5">
        {c.messages.map((m) => {
          if (m.side === 'SYSTEM') return <div key={m.id} className={`text-center text-[11.5px] ${m.internal ? 'text-amber-700' : 'text-ink-400'}`}>{m.internal && <Lock className="mr-1 inline h-3 w-3" />}{m.body} · {whenLong(m.at)}</div>;
          const ours = staff ? m.side === 'STAFF' : m.side === 'RESIDENT';
          return (
            <div key={m.id} className={`flex ${ours ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-[88%] sm:max-w-[75%]">
                <div className={`whitespace-pre-wrap break-words rounded-xl px-3.5 py-2.5 text-[13.5px] leading-relaxed ${m.internal ? 'border border-amber-300 bg-amber-50 text-amber-950' : ours ? 'bg-bronze-500 text-on-primary' : 'border border-ink-200 bg-white text-ink-900'}`}>
                  {m.internal && <div className="mb-1 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-amber-700"><Lock className="h-3 w-3" />Internal — the resident cannot see this</div>}
                  {m.body}
                </div>
                <div className={`mt-1 flex items-center gap-1 text-[11px] text-ink-400 ${ours ? 'justify-end' : ''}`}>
                  {m.mine ? 'You' : m.authorName ?? (m.side === 'STAFF' ? 'Lotus Living' : 'Resident')}
                  {!m.mine && m.side === 'STAFF' && !staff ? ' · Lotus Living' : ''} · {whenLong(m.at)}
                  {m.seenByResident === true && <CheckCheck className="ml-0.5 h-3.5 w-3.5 text-emerald-600" aria-label="Read by the resident" />}
                  {m.seenByResident === false && <Check className="ml-0.5 h-3.5 w-3.5" aria-label="Not read yet" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {c.closed && (
        <div className="flex flex-wrap items-center gap-2 border-t border-ink-100 bg-ink-50 px-4 py-3 text-[12.5px] text-ink-600 sm:px-5">
          <span className="flex-1">Closed by <b className="font-medium text-ink-800">{c.closed.byName ?? 'Lotus Living'}</b> on {whenLong(c.closed.at)}{c.closed.reason ? ` — ${c.closed.reason}` : ''}. {staff ? 'It is archived against the tenancy.' : 'Start a new message if you need us again.'}</span>
          {c.viewer.canClose && <Button size="sm" variant="secondary" icon="RotateCcw" disabled={reopen.isPending} onClick={() => reopen.mutate()}>Reopen</Button>}
        </div>
      )}

      {c.viewer.canReply && (
        <div className={`border-t px-3 py-3 sm:px-5 ${internal ? 'border-amber-200 bg-amber-50/60' : 'border-ink-100 bg-white'}`}>
          {c.viewer.canInternal && (
            <Segmented className="mb-2" value={internal ? 'internal' : 'reply'} onChange={(k) => setInternal(k === 'internal')}
              items={[{ key: 'reply', label: 'Reply to the resident' }, { key: 'internal', label: <><Lock className="h-3 w-3" />Internal note</> }]} />
          )}
          <div className="flex items-end gap-2">
            <Textarea
              value={text} onChange={(e) => setText(e.target.value)} rows={2}
              placeholder={internal ? 'A note for colleagues — the resident never sees this…' : 'Write a reply…'}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim()) send.mutate(); }}
              className="min-h-[44px] flex-1 resize-y"
            />
            <Button onClick={() => send.mutate()} disabled={!text.trim() || send.isPending} variant={internal ? 'accent' : 'primary'} className="shrink-0">
              {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}<span className="hidden sm:inline">{internal ? 'Add note' : 'Send'}</span>
            </Button>
          </div>
          {send.error && <div className="mt-2"><FormError error={send.error} /></div>}
          {c.viewer.canClose && c.status === 'OPEN' && (
            <div className="mt-2 flex items-center gap-2 text-[11.5px] text-ink-400">
              <button onClick={() => setClosing(true)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-medium text-ink-600 hover:bg-ink-100"><Archive className="h-3 w-3" />Close this conversation</button>
              <span className="hidden sm:inline">Done with it? Closing archives it against the tenancy.</span>
            </div>
          )}
        </div>
      )}

      {closing && <CloseDialog id={id} onClose={() => setClosing(false)} onDone={(d) => { setClosing(false); set(d); }} />}
    </div>
  );
}

function CloseDialog({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: (d: ConversationDetail) => void }) {
  const [reason, setReason] = useState('');
  const m = useMutation({ mutationFn: () => messagesApi.close(id, reason.trim()), onSuccess: onDone });
  return (
    <Modal open onClose={onClose} title="Close this conversation?"
      footer={<><Button variant="secondary" onClick={onClose}>Keep it open</Button><Button onClick={() => m.mutate()} disabled={m.isPending}>Close it</Button></>}>
      <p className="mb-4 text-[13px] text-ink-600">It stops taking replies and is archived against the tenancy. The resident sees that it was closed and why. You can reopen it at any time.</p>
      <Field label="Why, in a few words (optional)"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Tap replaced" maxLength={500} /></Field>
      {m.error && <div className="mt-3"><FormError error={m.error} /></div>}
    </Modal>
  );
}
